import styled from "styled-components";
import WARNING_CIRCLE_ICON from "assets/icons/warning_circle.svg";
import COOKIE_ICON from "assets/icons/marker_cookie.svg";
import {
  ICON_TYPE,
  NOTICE_COLOR,
} from "api/interfaces/noticeInterface.interface";
import { useMemo } from "react";
import TRIANGLE_CIRCLE_ICON from "assets/icons/triangle_warning.svg";
import DOCUMENT_ICON from "assets/icons/document.svg";
import { COLORS } from "styles/colors";
import { FLEX } from "styles/flex";
import { calcRem } from "styles/theme";
import { UnderlineText } from "components/atoms/text/Text";
import { IoMdClose } from "react-icons/io";
import * as mediaQuery from "components/MediaQuery";
import TinyButton from "components/atoms/buttons/TinyButton";

interface Props {
  message: string;
  color: NOTICE_COLOR;
  type: ICON_TYPE;
  onClickClose: (acceptCookie?: boolean) => void;
  isCookie?: boolean;
}

const LearnMoreComponent = () => {
  return (
    <UnderlineText
      color={COLORS.BUTTONS1}
      fontSize={12}
      onClick={() =>
        window.open(
          "https://digital-watchdog.com/page/statement-of-privacy",
          "_blank"
        )
      }
      className={mediaQuery.useIsMobile() ? "mobile-more-button" : ""}
      data-testid="element-notice-bar-tsx-element-underline-text-open"
    >
      Learn More
    </UnderlineText>
  );
};

export function NoticeBar(props: Props): JSX.Element {
  const { message, color, type, onClickClose } = props;

  const icon = useMemo(() => {
    switch (type) {
      case ICON_TYPE.INFO:
        return WARNING_CIRCLE_ICON;
      case ICON_TYPE.WARNING:
        return TRIANGLE_CIRCLE_ICON;
      case ICON_TYPE.DOCUMENT:
        return DOCUMENT_ICON;
      case ICON_TYPE.COOKIE:
        return COOKIE_ICON;
      default:
        return WARNING_CIRCLE_ICON;
    }
  }, [type]);

  return (
    <Container color={color} isCookie={props.isCookie}>
      <Wrapper>
        <img className="marker" src={icon} width="20" alt="Marker Icon" />
        <div className="notice-message">
          <span dangerouslySetInnerHTML={{ __html: message }} />

          <mediaQuery.Mobile>
            {props.isCookie && <LearnMoreComponent />}
          </mediaQuery.Mobile>
        </div>
        <mediaQuery.Default>
          {props.isCookie && <LearnMoreComponent />}
        </mediaQuery.Default>
        {props.isCookie && (
          <span className="accept-button">
            <TinyButton
              label="Accept Cookies"
              onClickBtn={() => onClickClose(true)}
              data-testid="element-notice-bar-tsx-element-tiny-button-click-close"
            />
          </span>
        )}
      </Wrapper>

      <CloseButton
        onClick={() => onClickClose(false)}
        className="close-button"
        data-testid="element-notice-bar-tsx-element-close-button-click-close"
      >
        <IoMdClose size={12} />
      </CloseButton>
    </Container>
  );
}

export const DefaultBarWrapper = styled.div<{ color: string }>`
  width: 100%;
  background-color: ${({ color }) => color};
  color: ${COLORS.WHITE};
  font-size: ${calcRem(14)};
  padding: 8px 20px;
  ${FLEX.FlexCenterCenter};
`;

const Container = styled(DefaultBarWrapper)<{ isCookie?: boolean }>`
  ${FLEX.FlexBetweenCenter};
  gap: 20px;

  ${({ isCookie }) =>
    isCookie &&
    `
      position: fixed;
      bottom: 0;
      z-index: 997;
  `}

  .close-button {
    ${({ isCookie, theme }) =>
      isCookie &&
      `
      color: ${theme.COLORS.WHITE}`}
  }

  .notice-message {
    overflow: hidden;

    > span {
      width: 100%;
      > p {
        white-space: normal;
        word-wrap: break-word;
        overflow-wrap: break-word;
      }

      ${({ isCookie, theme }) =>
        isCookie &&
        `
      color: ${theme.COLORS.WHITE}
    `}
    }

    .mobile-more-button {
      display: inline;
      margin-left: 10px;
    }

    a {
      color: yellow;
      text-decoration: underline;
      font-weight: bold;
    }
  }
`;

const Wrapper = styled.div`
  flex: 1;
  overflow: hidden;
  ${FLEX.FlexCenterCenter};
  gap: 16px;
  color: ${COLORS.WHITE};
  font-size: ${calcRem(12)};

  .marker {
    width: 16px;
    flex-shrink: 0;
  }

  ${mediaQuery.isDefault} {
    .accept-button {
      margin-left: 30px;
    }
  }
  ${mediaQuery.isMobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    .notice-message {
      padding-left: 25px;
    }
    .marker {
      position: absolute;
      left: 20px;
      top: 10px;
    }
  }
`;

const CloseButton = styled.button`
  /* position: absolute;
  right: 20px; */
  flex-shrink: 0;
  color: ${COLORS.WHITE};
`;
